import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../../environments/environment';
import { TraceService } from './trace.service';
import { LogEntry } from '../interfaces/log-entry';
import { LoggerConfig } from '../interfaces/logger-config';

/**
 * Supported log levels – ordered by severity.
 */
export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'fatal';

/**
 * Structured log entry that will be shipped to Grafana / Loki.
 */
const LEVEL_SEVERITY: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
  fatal: 4,
};

const DEFAULT_CONFIG: LoggerConfig = {
  minLevel: 'info',
  remoteEnabled: false,
  batchSize: 20,
  flushIntervalMs: 5000,
  labels: {
    app: 'dhe-frontend',
    environment: environment.environmentName,
    version: environment.version,
  },
};

@Injectable({ providedIn: 'root' })
export class LoggerService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);
  private readonly trace = inject(TraceService);

  private config: LoggerConfig = { ...DEFAULT_CONFIG };
  private buffer: LogEntry[] = [];
  private flushTimer: ReturnType<typeof setInterval> | null = null;

  // ---------- Public API --------------------------------------------------

  /**
   * Initialise the logger (call once from app bootstrap).
   * Overrides defaults with environment‑specific settings.
   */
  init(overrides?: Partial<LoggerConfig>): void {
    this.config = {
      ...DEFAULT_CONFIG,
      remoteEnabled: environment.production,
      ...(environment as any).logging,
      ...overrides,
    };

    // Set Loki endpoint from environment if available
    if ((environment as any).logging?.lokiEndpoint) {
      this.config.lokiEndpoint = (environment as any).logging.lokiEndpoint;
    }

    // Set labels from environment if available
    if ((environment as any).logging?.labels) {
      this.config.labels = {
        ...this.config.labels,
        ...((environment as any).logging?.labels as Record<string, string>),
      };
    }

    if (this.isBrowser && this.config.remoteEnabled) {
      this.startFlushTimer();
    }

    this.info('LoggerService', 'Logger initialised', {
      config: {
        minLevel: this.config.minLevel,
        remoteEnabled: this.config.remoteEnabled,
        environment: environment.environmentName,
      },
    });
  }

  debug(component: string, message: string, extra?: Record<string, unknown>): void {
    this.write('debug', component, message, extra);
  }

  info(component: string, message: string, extra?: Record<string, unknown>): void {
    this.write('info', component, message, extra);
  }

  warn(component: string, message: string, extra?: Record<string, unknown>): void {
    this.write('warn', component, message, extra);
  }

  error(
    component: string,
    message: string,
    error?: Error | unknown,
    extra?: Record<string, unknown>,
  ): void {
    const enriched: Record<string, unknown> = { ...extra };

    if (error instanceof Error) {
      enriched['stackTrace'] = error.stack;
      enriched['errorName'] = error.name;
    } else if (error !== undefined && error !== null) {
      enriched['errorDetail'] = String(error);
    }

    this.write('error', component, message, enriched);
  }

  fatal(
    component: string,
    message: string,
    error?: Error | unknown,
    extra?: Record<string, unknown>,
  ): void {
    const enriched: Record<string, unknown> = { ...extra };

    if (error instanceof Error) {
      enriched['stackTrace'] = error.stack;
      enriched['errorName'] = error.name;
    } else if (error !== undefined && error !== null) {
      enriched['errorDetail'] = String(error);
    }

    this.write('fatal', component, message, enriched);
  }

  /**
   * Force‑flush the buffer immediately.
   * Call this before navigation or page unload events.
   */
  flush(): void {
    if (this.buffer.length === 0) return;
    const batch = [...this.buffer];
    this.buffer = [];
    this.sendBatch(batch);
  }

  /**
   * Generate a unique correlation ID.
   * Handy for linking related log entries (e.g. one user flow).
   */
  generateCorrelationId(): string {
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substring(2, 10);
    return `${timestamp}-${random}`;
  }

  /**
   * Get / lazy‑create a session ID that survives page reloads.
   */
  getSessionId(): string {
    if (!this.isBrowser) return 'ssr';

    const STORAGE_KEY = 'dhe_log_session_id';
    let sid = sessionStorage.getItem(STORAGE_KEY);
    if (!sid) {
      sid = `${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 6)}`;
      sessionStorage.setItem(STORAGE_KEY, sid);
    }
    return sid;
  }

  /** Clean up timers (call on app destroy – rarely needed). */
  destroy(): void {
    if (this.flushTimer !== null) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
    this.flush();
  }

  // ---------- Internal ----------------------------------------------------

  private write(
    level: LogLevel,
    component: string,
    message: string,
    extra?: Record<string, unknown>,
  ): void {
    // Respect configured minimum level
    if (LEVEL_SEVERITY[level] < LEVEL_SEVERITY[this.config.minLevel]) {
      return;
    }

    // Enrich with trace context for Grafana trace-to-log correlation
    const traceCtx = this.trace.getTraceContext();

    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message: `[${component}] ${message}`,
      component,
      url: this.isBrowser ? window.location.href : undefined,
      sessionId: this.getSessionId(),
      traceId: traceCtx?.traceId,
      spanId: traceCtx?.spanId,
      extra,
    };

    // Console output (always, but use appropriate method)
    this.writeToConsole(entry);

    // Buffer for remote shipping
    if (this.config.remoteEnabled && this.isBrowser) {
      this.buffer.push(entry);
      if (this.buffer.length >= this.config.batchSize) {
        this.flush();
      }
    }
  }

  private writeToConsole(entry: LogEntry): void {
    const prefix = `[${entry.timestamp}] [${entry.level.toUpperCase()}]`;
    const sessionPart = entry.sessionId ? ` [sid:${entry.sessionId.substring(0, 8)}]` : '';

    switch (entry.level) {
      case 'debug':
        console.debug(prefix + sessionPart, entry.message, entry.extra ?? '');
        break;
      case 'info':
        console.info(prefix + sessionPart, entry.message, entry.extra ?? '');
        break;
      case 'warn':
        console.warn(prefix + sessionPart, entry.message, entry.extra ?? '');
        break;
      case 'error':
      case 'fatal':
        console.error(prefix + sessionPart, entry.message, entry.extra ?? '');
        break;
    }
  }

  private startFlushTimer(): void {
    if (this.flushTimer !== null) return;
    this.flushTimer = setInterval(() => this.flush(), this.config.flushIntervalMs);
  }

  /**
   * Send a batch of log entries to Loki (or custom aggregator).
   *
   * Loki expects the following JSON structure:
   * {
   *   "streams": [
   *     {
   *       "stream": { "label": "value", ... },
   *       "values": [ ["<epoch_ns>", "<json_line>"], ... ]
   *     }
   *   ]
   * }
   */
  private sendBatch(entries: LogEntry[]): void {
    const endpoint = this.config.lokiEndpoint;
    if (!endpoint) {
      // No remote endpoint configured – silently drop
      return;
    }

    // Build Loki‑compatible payload
    const values = entries.map((e) => {
      const ns = new Date(e.timestamp).getTime() * 1_000_000;
      return [String(ns), JSON.stringify(e)] as [string, string];
    });

    const payload = {
      streams: [
        {
          stream: {
            ...this.config.labels,
            level: values.length === 1 ? entries[0].level : undefined, // best-effort
          },
          values,
        },
      ],
    };

    // Clean up undefined labels
    const cleanPayload = JSON.parse(JSON.stringify(payload));

    // Use sendBeacon when available (fire‑and‑forget, won't block unloading)
    const body = JSON.stringify(cleanPayload);

    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      const success = navigator.sendBeacon(endpoint, blob);
      if (!success) {
        console.warn('[Logger] sendBeacon failed, falling back to fetch');
        this.fetchPost(endpoint, body);
      }
    } else {
      this.fetchPost(endpoint, body);
    }
  }

  private fetchPost(endpoint: string, body: string): void {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (this.config.lokiUsername && this.config.lokiPassword) {
      headers['Authorization'] =
        'Basic ' + btoa(`${this.config.lokiUsername}:${this.config.lokiPassword}`);
    }

    fetch(endpoint, {
      method: 'POST',
      headers,
      body,
      // keepalive lets the request complete even if the page is unloading
      keepalive: true,
    }).catch((err) => {
      // Silently fail – we don't want log shipping to cause errors
      console.warn('[Logger] Failed to ship logs:', err);
    });
  }
}
